import {
  Box,
  Button,
  ClockIcon,
  CoinIcon,
  ExternalLinkIcon,
  Heading,
  Link,
  RefreshIcon,
  Spinner,
  Text,
  useToast,
} from '@razorpay/blade/components';
import api from 'api/index';
import { AllocateBonusModal } from 'components/Bonus/components/AllocateBonusModal';
import { BonusTable } from 'components/Bonus/components/BonusTable';
import { bonusQueryKeys } from 'components/Bonus/data';
import { BonusRow } from 'components/Bonus/hooks/types';
import { useBonuses } from 'components/Bonus/hooks/useBonuses';
import { bonusStatus } from 'components/Bonus/types';
import {
  canDelete,
  canEdit,
  getDeleteBonusConfirmationText,
  isPayrollExecuted,
} from 'components/Bonus/utils';
import ActionModal from 'components/SalaryComponents/components/ActionModal';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import permissions from 'constants/permissions';
import { useSearchParams } from 'hooks/useSearchParams';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import userRoles from 'constants/userRoles';
import { getStaticMediaUrl } from 'utils/Urls';
import useHasPermission from 'utils/useHasPermission';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { AppError } from 'utils/AppError';
import noComponents from '../../../assets/noComponents.svg';
import { usePayDetails } from '../context/PayDetailsContext';
import ComponentContainer from '../components/generics/ComponentContainer';
import ComponentHeader from '../components/generics/ComponentHeader';
import ComponentSection from '../components/generics/ComponentSection';
import ComponentSectionItem from '../components/generics/ComponentSectionItem';
import { BONUS_HELP_ARTICLE_URL as HELP_ARTICLE_URL } from '../constants';

const BonusLoadingState = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    gap="spacing.5"
    height="100%">
    <Spinner accessibilityLabel="loading bonuses" />
    <Text>Loading...</Text>
  </Box>
);

const BonusErrorState = ({ onRetry }: { onRetry: () => void }) => (
  <EmptyListView
    title="There was an error while fetching the data"
    isError
    description={<Text color="surface.text.gray.muted">Please try again later</Text>}>
    <Box display="flex" flexDirection="column" gap="spacing.4" marginX="auto">
      <Box marginX="auto">
        <Button size="medium" onClick={onRetry} icon={RefreshIcon} iconPosition="left">
          Retry
        </Button>
      </Box>
    </Box>
  </EmptyListView>
);

const BonusEmptyState = ({
  canCreateBonus,
  firstName,
  onAllocateBonus,
}: {
  canCreateBonus: boolean;
  firstName: string | null;
  onAllocateBonus: () => void;
}) => (
  <Box
    display="flex"
    flexDirection="row"
    gap="spacing.8"
    height="100%"
    justifyContent="center"
    alignItems="center">
    <img src={getStaticMediaUrl(noComponents)} alt="no-bonuses" />
    <Box display="flex" flexDirection="column" gap="spacing.7">
      <Box display="flex" flexDirection="column" gap="spacing.2">
        <Heading weight="semibold">No bonuses assigned yet</Heading>
        {canCreateBonus && firstName && (
          <Text color="surface.text.gray.muted">Allocate a bonus to {firstName}</Text>
        )}
      </Box>
      <Box display="flex" flexDirection="row" gap="spacing.5" alignItems="center">
        {canCreateBonus && <Button onClick={onAllocateBonus}>+ Allocate Bonus</Button>}
        <Link href={HELP_ARTICLE_URL} target="_blank" icon={ExternalLinkIcon} iconPosition="right">
          Learn More
        </Link>
      </Box>
    </Box>
  </Box>
);

const BonusView = () => {
  const queryClient = useQueryClient();
  const { urlParams } = useSearchParams();
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId);
  const loggedInUserRole = useSelector(loggedInUserSelectors.role);
  const isAdmin = loggedInUserRole === userRoles.ADMIN;
  const hasBonusEditPermission = useHasPermission([permissions.BONUS_EDIT]);
  const { userData } = usePayDetails();
  const toast = useToast();

  const requestedUserId = urlParams.get('userId');
  const loggedInUserIdString = loggedInUserId?.toString() ?? null;
  // Admins and bonus operators (BONUS_EDIT) may view the selected employee;
  // plain employees stay pinned to their own record (ISS-3653128).
  const canManageRequestedUser = isAdmin || hasBonusEditPermission;
  const userId = canManageRequestedUser
    ? requestedUserId || loggedInUserIdString
    : loggedInUserIdString;
  const firstName = userData?.name ? userData.name.split(' ')[0] : null;

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedBonusId, setSelectedBonusId] = useState<string | number | null>(null);
  const [allocateModalOpen, setAllocateModalOpen] = useState(false);
  const [editBonusId, setEditBonusId] = useState<string | number | null>(null);

  const { bonuses, isLoading, error, refetch } = useBonuses(userId);

  const invalidateBonuses = () =>
    queryClient.invalidateQueries([bonusQueryKeys.rearchBonusAssignments, userId]);

  const onDeleteSuccess = (bonusName: string | null) => {
    invalidateBonuses();
    toast.show({
      content: `${bonusName ?? 'Bonus'} deleted successfully!`,
      color: 'positive',
      autoDismiss: true,
      duration: 5000,
    });
    setDeleteModalOpen(false);
    setSelectedBonusId(null);
  };

  const selectedBonus = bonuses.find((b) => b.id === selectedBonusId);

  const siblingsOf = (bonus: BonusRow) =>
    bonus.assignment_id
      ? bonuses.filter((candidate) => candidate.assignment_id === bonus.assignment_id)
      : [bonus];

  const hasExecutedSibling = (bonus: BonusRow) => siblingsOf(bonus).some(isPayrollExecuted);

  const affectedSiblings = selectedBonus ? siblingsOf(selectedBonus) : [];
  const affectedTotal = affectedSiblings.reduce((total, bonus) => total + (bonus.amount ?? 0), 0);

  const onDeleteFailure = (message: string) => {
    toast.show({
      content: message,
      color: 'negative',
      autoDismiss: true,
      duration: 5000,
    });
    setDeleteModalOpen(false);
    setSelectedBonusId(null);
  };

  const cancelBonusAssignmentMutation = useMutation(
    api.bonusAssignment.mutations.cancelBonusAssignment,
    {
      onSuccess: (data) => {
        const result = data?.payrollCancelBonusAssignment;
        if (result?.success) {
          onDeleteSuccess(selectedBonus?.bonus_name ?? null);
          return;
        }
        onDeleteFailure(result?.message || 'Could not delete this bonus. Please try again.');
      },
      onError: (error: AppError) => {
        onDeleteFailure(parseGraphqlErrorFromExtensions(error));
      },
    },
  );
  const activeBonuses = bonuses.filter(
    (bonus) => bonus.status?.toString() !== bonusStatus.BONUS_STATUS_PAID,
  );
  const vestedBonuses = bonuses.filter(
    (bonus) => bonus.status?.toString() === bonusStatus.BONUS_STATUS_PAID,
  );
  const canCreateBonus = isAdmin && hasBonusEditPermission;

  const handleAllocateBonus = () => {
    setAllocateModalOpen(true);
  };

  const handleAssignSuccess = invalidateBonuses;

  const handleEdit = (id: string | number) => {
    setEditBonusId(id);
  };

  const handleDeleteClick = (id: string | number) => {
    setSelectedBonusId(id);
    setDeleteModalOpen(true);
  };

  const renderBonusContent = () => {
    if (isLoading) {
      return <BonusLoadingState />;
    }

    if (error) {
      return <BonusErrorState onRetry={() => refetch()} />;
    }

    if (bonuses.length === 0) {
      return (
        <BonusEmptyState
          canCreateBonus={canCreateBonus}
          firstName={firstName}
          onAllocateBonus={handleAllocateBonus}
        />
      );
    }

    return (
      <Box display="flex" flexDirection="column" gap="spacing.7">
        {isAdmin ? (
          <>
            <ComponentHeader
              icon={<CoinIcon size="medium" color="surface.icon.gray.normal" />}
              iconBackground="surface.background.gray.moderate"
              title="Bonuses"
              description="Perks that employees enjoy over and above their salary"
              button={
                canCreateBonus ? (
                  <Button variant="tertiary" marginLeft="auto" onClick={handleAllocateBonus}>
                    + New Bonus
                  </Button>
                ) : undefined
              }
            />
            <BonusTable
              bonuses={bonuses}
              showActions={hasBonusEditPermission}
              onEditClick={handleEdit}
              onDeleteClick={handleDeleteClick}
              canEditRow={(bonus) => !!canEdit(hasBonusEditPermission, bonus)}
              canDeleteRow={(bonus) =>
                !!canDelete(hasBonusEditPermission, bonus) && !hasExecutedSibling(bonus)
              }
            />
          </>
        ) : (
          <>
            {activeBonuses.length > 0 && (
              <>
                <ComponentHeader
                  icon={<ClockIcon size="medium" color="surface.icon.gray.normal" />}
                  iconBackground="surface.background.gray.moderate"
                  title="Active"
                  description="Pending grants and ongoing obligations"
                />
                <BonusTable bonuses={activeBonuses} showActions={false} />
              </>
            )}

            {vestedBonuses.length > 0 && (
              <>
                <ComponentHeader
                  icon={<CoinIcon size="medium" color="surface.icon.gray.normal" />}
                  iconBackground="surface.background.gray.moderate"
                  title="Vested"
                  description="History of earned bonuses and completed grants"
                />
                <BonusTable bonuses={vestedBonuses} showActions={false} />
              </>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <>
      <ComponentContainer>
        <ComponentSection>
          <ComponentSectionItem>{renderBonusContent()}</ComponentSectionItem>
        </ComponentSection>
      </ComponentContainer>

      <ActionModal
        isOpen={deleteModalOpen}
        onDismiss={() => {
          setDeleteModalOpen(false);
          setSelectedBonusId(null);
        }}
        headingText={`Delete ${selectedBonus?.bonus_name ?? 'Bonus'}?`}
        submitButton={
          <Button
            size="medium"
            color="negative"
            isLoading={cancelBonusAssignmentMutation.isLoading}
            onClick={() => {
              const assignmentId = selectedBonus?.assignment_id;
              if (assignmentId) {
                cancelBonusAssignmentMutation.mutate({ input: { id: assignmentId } });
              }
            }}>
            Delete now
          </Button>
        }>
        <Box display="flex" flexDirection="column" gap="spacing.2">
          <Text size="small" color="surface.text.gray.muted" weight="medium">
            What will happen?
          </Text>
          <Text color="surface.text.gray.subtle">
            {getDeleteBonusConfirmationText(
              selectedBonus?.bonus_name ?? null,
              affectedSiblings.length,
              affectedTotal,
              selectedBonus?.amount,
              firstName,
            )}
          </Text>
        </Box>
      </ActionModal>

      {/* Edit reuses the allocate wizard, prefilled from the row; keyed so a
          different row remounts the form with fresh initial state. */}
      {editBonusId !== null && (
        <AllocateBonusModal
          key={String(editBonusId)}
          isOpen
          onClose={() => setEditBonusId(null)}
          onAssignSuccess={invalidateBonuses}
          employeeName={firstName ?? ''}
          peopleId={userId}
          userOrgId={userData?.id != null ? String(userData.id) : null}
          editBonus={bonuses.find((b) => b.id === editBonusId) ?? null}
        />
      )}

      <AllocateBonusModal
        isOpen={allocateModalOpen}
        onClose={() => setAllocateModalOpen(false)}
        onAssignSuccess={handleAssignSuccess}
        employeeName={firstName ?? ''}
        peopleId={userId}
        userOrgId={userData?.id != null ? String(userData.id) : null}
      />
    </>
  );
};

export default BonusView;
